import { fetchFilteredAdvertisingData } from '../shared/mockData';
import { getAdvertisingFilterStateForSheet, filterStateManager, initializeFilterStateManager } from '../shared/filterStateManager';

// Write large 2D arrays in smaller batches to avoid payload limits on web/Mac
async function writeValuesInChunks(
  sheet: Excel.Worksheet,
  startRow: number,
  columnsLength: number,
  data: any[][],
  context: Excel.RequestContext,
  chunkSize: number = 500
) {
  const lastCol = getExcelColumnLetter(columnsLength);
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    const from = startRow + i;
    const to = from + chunk.length - 1;
    const range = sheet.getRange(`A${from}:${lastCol}${to}`);
    range.values = chunk;
    await context.sync();
  }
}

export async function updateCopiedAdvertisingSheet(
	context: Excel.RequestContext,
	copiedSheet: Excel.Worksheet,
	preFetchedData?: { columns: string[]; data: any[][]; appliedSummary: string }
): Promise<void> {
	copiedSheet.load('name');
	await context.sync();

	await renderAdvertisingHeader(context, copiedSheet);

	let filtersForApi: any = {};
	let appliedSummary: string = '';
	let columns: string[] = [];
	let data: any[][] = [];
	
	// If data is pre-fetched, use it directly
	if (preFetchedData) {
		columns = preFetchedData.columns;
		data = preFetchedData.data;
		appliedSummary = preFetchedData.appliedSummary;
	} else {
		// Otherwise, fetch data as before (for backward compatibility)
		copiedSheet.load("name");
		await context.sync();
		const sheetName = copiedSheet.name;
		
		try {
			if (!filterStateManager.isInitialized()) {
				await initializeFilterStateManager();
			}
			const p = await filterStateManager.hydrateAdvertisingFiltersForSheet(sheetName);
			
			if (p.startDate && p.endDate) {
				const start = typeof p.startDate === 'string' ? p.startDate : p.startDate?.toISOString().split('T')[0];
				const end = typeof p.endDate === 'string' ? p.endDate : p.endDate?.toISOString().split('T')[0];
				if (start && end) {
					filtersForApi.startDate = start;
					filtersForApi.endDate = end;
				}
			}
			if (p.brand && p.brand.length > 0) filtersForApi.brands = p.brand;
			if (p.skus && p.skus.length > 0) filtersForApi.asins = p.skus;
			if (p.channel && p.channel !== 'all') filtersForApi.channels = [p.channel];
			if (p.keyword && p.keyword.length > 0) filtersForApi.keyword_contains = p.keyword;
			if (p.campaign && p.campaign.length > 0) filtersForApi.campaign_name = p.campaign;

			const parts: string[] = [];
			if (p.keyword && p.keyword.length > 0) parts.push(`Keyword: ${Array.isArray(p.keyword) ? p.keyword.join(', ') : p.keyword}`);
			if (p.campaign && p.campaign.length > 0) parts.push(`Campaign: ${p.campaign.join(', ')}`);
			if (p.brand && p.brand.length > 0) parts.push(`Brand: ${p.brand.join(', ')}`);
			if (p.skus && p.skus.length > 0) parts.push(`ASIN/SKU: ${p.skus.join(', ')}`);
			if (p.startDate && p.endDate) {
				const start = typeof p.startDate === 'string' ? p.startDate : p.startDate?.toISOString().split('T')[0];
				const end = typeof p.endDate === 'string' ? p.endDate : p.endDate?.toISOString().split('T')[0];
				parts.push(`Dates: ${start} - ${end}`);
			}
			appliedSummary = parts.length > 0 ? `Filters: ${parts.join(' • ')}` : '';
		} catch (error) {
			console.error('Error loading filters for sheet:', error);
		}

		const resp = await fetchFilteredAdvertisingData(filtersForApi);
		columns = resp?.columns || [];
		data = resp?.data || [];
	}

	if (columns.length === 0) {
		await context.sync();
		return;
	}

	const lastCol = getExcelColumnLetter(columns.length);

	try {
		const r = copiedSheet.getRange('A3:K3');
		try { r.unmerge(); } catch (_) {}
		r.merge();
		copiedSheet.getRange('A3').values = [[appliedSummary]];
		r.format.font.size = 10;
		r.format.font.italic = true as any;
		r.format.font.color = '#666666';
		r.format.horizontalAlignment = 'Left';
		r.format.verticalAlignment = 'Center';
		r.format.fill.color = '#F0F4F8';
	} catch (_) {}

	const headerRange = copiedSheet.getRange(`A4:${lastCol}4`);
	headerRange.values = [columns];
	headerRange.format.fill.color = '#4472C4';
	headerRange.format.font.color = 'white';
	headerRange.format.font.bold = true;

	if (data.length > 0) {
		try {
			const idColIdx = columns.indexOf('campaign_id');
			if (idColIdx >= 0) {
				const idColLetter = getExcelColumnLetter(idColIdx + 1);
				copiedSheet.getRange(`${idColLetter}:${idColLetter}`).numberFormat = [["@"]];
			}
		} catch (_) {}

		await writeValuesInChunks(
			copiedSheet,
			5,
			columns.length,
			data,
			context
		);
		copiedSheet.getRange(`A4:${lastCol}${4 + data.length}`).format.autofitColumns();
	}

	const usedRange = copiedSheet.getUsedRange();
	usedRange.load('rowCount, columnCount');
	await context.sync();
	const validRows = Math.max(5, 4 + data.length + 1); // header row (4) + data rows + at least one extra row
	const totalRows = usedRange.rowCount;
	if (validRows < totalRows) {
		const clearRange = copiedSheet.getRangeByIndexes(validRows, 0, totalRows - validRows, usedRange.columnCount);
		clearRange.clear();
	}

	await context.sync();
}

async function renderAdvertisingHeader(
	context: Excel.RequestContext,
	sheet: Excel.Worksheet
): Promise<void> {
	const headerBandRange = sheet.getRange('A1:K2');
	headerBandRange.format.fill.color = '#F0F4F8';
	headerBandRange.format.verticalAlignment = 'Center';

	const titleRange = sheet.getRange('A1:D2');
	titleRange.merge();
	sheet.getRange('A1').values = [['Advertising Raw Data']];
	titleRange.format.font.size = 16;
	titleRange.format.font.bold = true;
	titleRange.format.font.color = '#1F4788';
	titleRange.format.horizontalAlignment = 'Left';
	titleRange.format.fill.color = '#F0F4F8';

	try { /* no indent when starting at A */ } catch (_) {}

	// Third row background
	const thirdRowRange = sheet.getRange('A3:K3');
	thirdRowRange.format.fill.color = '#F0F4F8';

	await context.sync();
}

function getExcelColumnLetter(columnNumber: number): string {
	let n = Math.max(1, Math.floor(columnNumber));
	let columnName = '';
	while (n > 0) {
		const rem = (n - 1) % 26;
		columnName = String.fromCharCode(65 + rem) + columnName;
		n = Math.floor((n - 1) / 26);
	}
	return columnName;
}


